import type { ManageLocation, ManagePayload } from '../types';

export const VENUE_TZ = 'Europe/Amsterdam';

export function venueDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', {
    timeZone: VENUE_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function venueTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: VENUE_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function venueDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    timeZone: VENUE_TZ,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// Google Maps search URL for the venue. The confirmation email dropped its
// Maps link for deliverability reasons — this page carries it instead.
// Returns null until fn_manage_fetch exposes the address fields.
export function mapsUrlFor(location: ManageLocation): string | null {
  if (!location.address_line) return null;
  const q = [
    location.name,
    location.address_line,
    [location.postal_code, location.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function venueDayString(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function todayVenueString(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: VENUE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// Single source of truth for whether self-service reschedule is available and,
// if not, why. The reschedule CTA (top of the booking summary) shows on 'ok';
// the other states drive the contextual message under the summary (Actions).
export type RescheduleState = 'ok' | 'cancelled' | 'arrived' | 'cutoff' | 'unsupported';

export function rescheduleState(payload: ManagePayload): RescheduleState {
  const lc = payload.booking.lifecycle_status;
  if (lc === 'cancelled') return 'cancelled';
  if (lc === 'arrived') return 'arrived';
  if (!payload.is_within_cutoff) return 'cutoff';
  if (payload.reschedule_supported === false) return 'unsupported';
  return 'ok';
}
